/* The app's UI sounds — the pair the generation queue has always played, lifted here so they
 * have one definition instead of one per surface.
 *
 * They were written inside GenerationQueue.jsx, which is the panel most people keep minimised,
 * so in practice they were attached to a component nobody looks at. Nothing about them is
 * queue-specific: they mark "a video was asked for" and "a video came back", which is the same
 * pair of events the session pages needed. Kept verbatim rather than re-synthesised, because
 * these are the sounds the product already has and a second, slightly different laser would
 * just be a second, slightly different laser.
 *
 * A fresh AudioContext per call, closed on a timer. That is how the queue has always done it
 * and it works: the completion sound fires from a poll rather than a click, but by then the
 * page has sticky activation from the click that started the generation, so playback is
 * allowed.
 */

// Same key the queue's speaker toggle writes. Muting there mutes everything.
const CHIME_ENABLED_KEY = "magi_queue_chime_enabled";

export const soundsEnabled = () => {
  try {
    return localStorage.getItem(CHIME_ENABLED_KEY) !== "false";
  } catch {
    return true;
  }
};

// The laser. Sawtooth swept 1800 → 200Hz, with a sine ping falling behind it.
export const playCompletionChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const t = ctx.currentTime;
    const laser = ctx.createOscillator();
    const laserGain = ctx.createGain();
    laser.type = 'sawtooth';
    laser.frequency.setValueAtTime(1800, t);
    laser.frequency.exponentialRampToValueAtTime(200, t + 0.15);
    laserGain.gain.setValueAtTime(0.13, t);
    laserGain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    laser.connect(laserGain); laserGain.connect(ctx.destination);
    laser.start(t); laser.stop(t + 0.25);
    const ping = ctx.createOscillator();
    const pingGain = ctx.createGain();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(1200, t + 0.12);
    ping.frequency.exponentialRampToValueAtTime(800, t + 0.5);
    pingGain.gain.setValueAtTime(0, t);
    pingGain.gain.linearRampToValueAtTime(0.1, t + 0.14);
    pingGain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    ping.connect(pingGain); pingGain.connect(ctx.destination);
    ping.start(t + 0.12); ping.stop(t + 0.6);
    setTimeout(() => ctx.close(), 1000);
  } catch (e) {}
};

// The launch. A rising sine with a square click on top of it.
export const playStartSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 0.15);
    gain.gain.setValueAtTime(0.1, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t); osc.stop(t + 0.25);
    const click = ctx.createOscillator();
    const clickGain = ctx.createGain();
    click.type = 'square';
    click.frequency.setValueAtTime(600, t + 0.13);
    clickGain.gain.setValueAtTime(0, t);
    clickGain.gain.linearRampToValueAtTime(0.06, t + 0.13);
    clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    click.connect(clickGain); clickGain.connect(ctx.destination);
    click.start(t + 0.13); click.stop(t + 0.25);
    setTimeout(() => ctx.close(), 500);
  } catch (e) {}
};

/* THE SAME FINISHED JOB IS NOTICED IN MORE THAN ONE PLACE. The queue panel watches every
 * job's state, the Workspace tile's dial watches its own, and a session page watches its
 * entries — so a video landing while all three are mounted fired the laser three times, on
 * top of each other. This plays it for an id once per page load and is a no-op after that,
 * so each surface can just say "this one finished" without knowing who else is looking.
 * Also carries the mute check, since every caller had to remember it separately. */
/* PERSISTED, because "once per page load" is once per REFRESH. The dial fires on `done`
 * being true rather than on the transition to true — deliberately, so a job that landed while
 * you were away still announces itself — and with an in-memory set every refresh re-announced
 * every finished job on screen. On the reel page that is a beep per reload, forever.
 *
 * localStorage, not sessionStorage: coming back tomorrow to a video that finished last night
 * is the same "already heard it" case, and a new tab is not a reason to hear it again.
 *
 * Bounded. Job ids accumulate and nothing would ever remove them, so this keeps the most
 * recent MAX_REMEMBERED and drops the oldest — a ring buffer, not a leak. The order is
 * insertion order, which is what a Set gives for free. */
const CHIMED_KEY = "magi_chimed_job_ids";
const MAX_REMEMBERED = 500;

const loadChimed = () => {
  try {
    const raw = localStorage.getItem(CHIMED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();          // private mode, quota, corrupt value — chime, never throw
  }
};

const chimed = loadChimed();

const rememberChimed = (id) => {
  chimed.add(id);
  try {
    // Trim in the Set as well as in storage, or the in-memory copy grows without bound in a
    // long-lived tab even though what is persisted stays capped.
    while (chimed.size > MAX_REMEMBERED) chimed.delete(chimed.values().next().value);
    localStorage.setItem(CHIMED_KEY, JSON.stringify([...chimed]));
  } catch {
    // Storage unavailable: the in-memory Set still dedupes within this page load, which is
    // the behaviour this had before it was persisted.
  }
};

export const playCompletionChimeOnce = (id) => {
  if (!id) return;                      // no id, no dedupe — better silent than a stutter
  if (chimed.has(id)) return;
  rememberChimed(id);
  if (soundsEnabled()) playCompletionChime();
};

export default { playCompletionChime, playCompletionChimeOnce, playStartSound, soundsEnabled };
